import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDateTime, fmtDate } from "@/components/shared";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [investments, setInvestments] = useState([]);
  useEffect(() => {
    api.get(`/admin/users/${id}`).then((r) => {
      setData(r.data);
      api.get("/admin/investments", { params: { status: "all", search: r.data.profile?.email } }).then((x) => setInvestments(x.data.items.filter((i) => i.user_id === id))).catch(() => {});
    });
  }, [id]);
  if (!data) return <Loader />;
  const p = data.profile;

  const Table = ({ rows, cols }) => rows.length ? (
    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[500px]">
      <thead><tr className="text-left text-slate-500 border-b border-white/5">{cols.map((c) => <th key={c} className="pb-2 font-medium">{c}</th>)}</tr></thead>
      <tbody>{rows}</tbody>
    </table></div>
  ) : <p className="text-sm text-slate-500 py-6 text-center">No records</p>;

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <button onClick={() => nav("/admin/users")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="w-4 h-4" /> Back to Users</button>
      <div className="glass rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold text-2xl">{p.full_name[0]?.toUpperCase()}</div>
        <div className="flex-1">
          <h1 className="font-heading text-xl text-white">{p.full_name}</h1>
          <p className="text-sm text-slate-400">{p.email} · {p.mobile}</p>
          <p className="text-xs text-slate-600 font-mono mt-1">ID: {p.id} · Code: {p.referral_code} · Joined {fmtDate(p.created_at)}</p>
        </div>
        <StatusBadge status={p.account_status} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[["Balance", p.available_balance], ["Deposit", p.total_deposit], ["Earnings", p.total_earnings], ["Withdrawal", p.total_withdrawal], ["Ref Rewards", p.total_referral_reward]].map(([l, v]) => (
          <div key={l} className="glass rounded-xl p-4"><p className="text-xs text-slate-500">{l}</p><p className="font-mono text-lg text-white mt-1">{fmt(v)}</p></div>
        ))}
      </div>

      <div className="glass rounded-2xl p-6">
        <Tabs defaultValue="deposits">
          <TabsList className="bg-slate-900/60 flex-wrap h-auto">
            {["deposits", "investments", "withdrawals", "referrals", "transactions", "verification"].map((t) => (
              <TabsTrigger key={t} value={t} className="capitalize data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300">{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="deposits" className="mt-4">
            <Table cols={["Amount", "Network", "Status", "Date"]} rows={data.deposits.map((d) => (
              <tr key={d.id} className="border-b border-white/5"><td className="py-2 font-mono text-white">{fmt(d.amount)}</td><td className="py-2 text-slate-300">{d.network}</td><td className="py-2"><StatusBadge status={d.status} /></td><td className="py-2 text-slate-500">{fmtDateTime(d.created_at)}</td></tr>
            ))} />
          </TabsContent>
          <TabsContent value="investments" className="mt-4">
            <Table cols={["Plan", "Principal", "Daily ROI", "Working days", "ROI earned", "Next ROI / Matured", "Status"]} rows={(investments || []).map((i) => (
              <tr key={i.id} className="border-b border-white/5" data-testid={`user-investment-${i.id}`}><td className="py-2 text-slate-300">{i.plan_name}</td><td className="py-2 font-mono text-white">{fmt(i.amount)}</td><td className="py-2 font-mono text-cyan-300">{fmt(i.daily_roi)}</td><td className="py-2 font-mono text-slate-300">{i.days_credited} / {i.duration_days}</td><td className="py-2 font-mono text-emerald-300">{fmt(i.roi_earned)}</td><td className="py-2 text-slate-500 text-xs">{fmtDateTime(i.status === "active" ? i.next_credit_at : i.completed_at)}</td><td className="py-2"><StatusBadge status={i.status === "completed" ? "matured" : i.status} /></td></tr>
            ))} />
          </TabsContent>
          <TabsContent value="withdrawals" className="mt-4">
            <Table cols={["Amount", "Network", "Status", "Date"]} rows={data.withdrawals.map((d) => (
              <tr key={d.id} className="border-b border-white/5"><td className="py-2 font-mono text-white">{fmt(d.amount)}</td><td className="py-2 text-slate-300">{d.network}</td><td className="py-2"><StatusBadge status={d.status} /></td><td className="py-2 text-slate-500">{fmtDateTime(d.created_at)}</td></tr>
            ))} />
          </TabsContent>
          <TabsContent value="referrals" className="mt-4">
            <Table cols={["Referred User ID", "Status", "Date"]} rows={data.referrals.map((r) => (
              <tr key={r.id} className="border-b border-white/5"><td className="py-2 font-mono text-xs text-slate-400">{r.referred_user_id.slice(0, 8)}</td><td className="py-2"><StatusBadge status={r.status} /></td><td className="py-2 text-slate-500">{fmtDateTime(r.created_at)}</td></tr>
            ))} />
          </TabsContent>
          <TabsContent value="transactions" className="mt-4">
            <Table cols={["Type", "Amount", "Status", "Date"]} rows={data.transactions.map((t) => (
              <tr key={t.id} className="border-b border-white/5"><td className="py-2 capitalize text-slate-200">{t.type.replace("_", " ")}</td><td className="py-2 font-mono text-white">{fmt(t.amount)}</td><td className="py-2"><StatusBadge status={t.status} /></td><td className="py-2 text-slate-500">{fmtDateTime(t.created_at)}</td></tr>
            ))} />
          </TabsContent>
          <TabsContent value="verification" className="mt-4">
            {data.verification ? (
              <div className="text-sm text-slate-300 space-y-1">
                <p>Status: <StatusBadge status={data.verification.status} /></p>
                <p>Name: {data.verification.full_name}</p>
                <p>ID Type: {data.verification.id_type} · {data.verification.id_number}</p>
                <p>Address: {data.verification.address}</p>
              </div>
            ) : <p className="text-sm text-slate-500 py-6 text-center">No KYC submitted</p>}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
